import React from 'react';
import { LiveChatAccountAccess, getLiveChatSkill, getLiveChatParams } from '../domain';
import { sendStatisticsEvent } from '../services';
import { boundPosition } from '../utils';
export const useLivechatProps = ({ account, onClose, onOpen, isOpen: initialIsOpen, language, statisticsUrl = '/panel-api/account/live-chat/', onUpgrade: _onUpgrade, buttonVariant, buttonPosition, liveChatWidgetId, statisticsEnabled = true, }) => {
    const [isOpen, setIsOpen] = React.useState(initialIsOpen || false);
    const access = (account === null || account === void 0 ? void 0 : account.livechatAccess) || LiveChatAccountAccess.Granted;
    const openChat = React.useCallback(() => {
        setIsOpen(true);
        onOpen === null || onOpen === void 0 ? void 0 : onOpen({ account });
        if (!statisticsEnabled) {
            return;
        }
        if (!account || account.livechatAccess === LiveChatAccountAccess.Granted) {
            sendStatisticsEvent(statisticsUrl, {
                name: 'window_opened',
                account: account || null,
            });
        }
    }, [account, onOpen, statisticsUrl, statisticsEnabled]);
    const hideChat = React.useCallback(() => {
        setIsOpen(false);
        onClose === null || onClose === void 0 ? void 0 : onClose({ account });
    }, [onClose, account]);
    React.useEffect(() => {
        if (initialIsOpen === undefined || access === LiveChatAccountAccess.Blocked) {
            return;
        }
        if (initialIsOpen) {
            openChat();
        }
        else {
            hideChat();
        }
    }, [access, hideChat, initialIsOpen, openChat]);
    const skill = React.useMemo(() => getLiveChatSkill({
        language,
        account,
    }), [account, language]);
    const params = React.useMemo(() => getLiveChatParams({
        language,
        account,
        buttonVariant,
    }), [account, language, buttonVariant]);
    const onVisibilityChanged = React.useCallback((data) => {
        if (data.visibility === 'maximized') {
            const liveChat = document.getElementById(liveChatWidgetId);
            if (liveChat && buttonVariant === 'bubble' && buttonPosition) {
                const boundedPosition = boundPosition(buttonPosition, liveChat);
                Object.assign(liveChat.style, {
                    left: `${boundedPosition.x}px`,
                    top: `${boundedPosition.y}px`,
                });
            }
            return;
        }
        hideChat();
    }, [buttonPosition, buttonVariant, hideChat, liveChatWidgetId]);
    const onNewEvent = React.useCallback((event) => {
        if (!statisticsEnabled) {
            return;
        }
        if (event.author.type === 'customer') {
            sendStatisticsEvent(statisticsUrl, {
                name: 'customer_message_submitted',
                account: account || null,
                timestamp: event.timestamp,
            });
        }
        if (event.author.type === 'agent') {
            sendStatisticsEvent(statisticsUrl, {
                name: 'agent_message_submitted',
                account: account || null,
                timestamp: event.timestamp,
            });
        }
    }, [account, statisticsUrl, statisticsEnabled]);
    const onFormSubmitted = React.useCallback((data) => {
        if (!statisticsEnabled) {
            return;
        }
        sendStatisticsEvent(statisticsUrl, {
            name: 'welcome_form_filled',
            account: account || null,
            payload: {
                type: data.type,
            },
        });
    }, [account, statisticsUrl, statisticsEnabled]);
    const onUpgrade = React.useCallback(() => {
        _onUpgrade === null || _onUpgrade === void 0 ? void 0 : _onUpgrade({ account });
    }, [_onUpgrade, account]);
    return {
        onVisibilityChanged,
        params,
        skill,
        openChat,
        hideChat,
        access,
        isOpen,
        onNewEvent,
        onFormSubmitted,
        onUpgrade,
    };
};
